import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import FormHelperText from '@mui/material/FormHelperText';
import LinearProgress from '@mui/material/LinearProgress';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { orderApiV2 } from '../../../Api';

const FILTER_MODE = {
    MONTH: 'MONTH',
    CUSTOM: 'CUSTOM',
};

const FILE_TYPES = ['CSV']; // future: add XLSX, PDF

const FIELD_PREF_STORAGE_KEY = 'trea_export_orders_fields';

const thisMonth = dayjs();

function monthDiff(a, b) {
    if (!a || !b) return 0;
    const start = dayjs(a);
    const end = dayjs(b);
    return end.diff(start, 'month', true);
}

const ExportOrdersModal = ({ open, onClose }) => {
    const [filterMode, setFilterMode] = useState(FILTER_MODE.MONTH);
    const [monthYear, setMonthYear] = useState(thisMonth);

    const [startDate, setStartDate] = useState(thisMonth.startOf('month'));
    const [endDate, setEndDate] = useState(thisMonth.endOf('month'));

    const [fieldGroups, setFieldGroups] = useState(null); // backend meta groups
    const [isFieldMetaLoading, setIsFieldMetaLoading] = useState(false);

    const [selectedFields, setSelectedFields] = useState([]);
    const [fileType, setFileType] = useState('CSV');

    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState(null);

    const flatFieldOptions = useMemo(() => {
        if (!fieldGroups) return [];
        const result = [];
        fieldGroups.forEach(group => {
            if (!group || !group.fields) return;
            group.fields.forEach(field => {
                result.push({
                    groupKey: group.key,
                    groupLabel: group.label,
                    value: field.key,
                    label: field.label,
                    isDefault: !!field.isDefault,
                });
            });
        });
        return result;
    }, [fieldGroups]);


    const dateError = useMemo(() => {
        if (filterMode === FILTER_MODE.MONTH) {
            if (!monthYear) return 'Please select a month and year.';
            return null;
        }

        if (!startDate || !endDate) {
            return 'Please select both start and end dates.';
        }
        if (dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
            return 'End date must be on or after start date.';
        }
        if (monthDiff(startDate, endDate) > 6) {
            return 'Date range cannot exceed 6 months.';
        }
        return null;
    }, [filterMode, monthYear, startDate, endDate]);

    const fieldsError = useMemo(() => {
        if (isFieldMetaLoading || !fieldGroups) {
            return null;
        }

        if (!selectedFields || selectedFields.length === 0) {
            return 'Please select at least one field.';
        }
        return null;
    }, [selectedFields, isFieldMetaLoading, fieldGroups]);

    const hasError = !!dateError || !!fieldsError || isFieldMetaLoading || !fieldGroups;

    const handleClose = () => {
        if (isExporting) return;
        setError(null);
        onClose && onClose();
    };

    useEffect(() => {
        if (!open) return;
        // Only fetch meta the first time we open, or if it was never loaded
        if (fieldGroups) return;

        const fetchMeta = async () => {
            try {
                setIsFieldMetaLoading(true);
                const res = await orderApiV2.get('/export/meta');
                const groups = res.data?.groups || [];
                setFieldGroups(groups);

                // Build default selection based on localStorage or backend isDefault flags
                let initialFields = [];
                try {
                    const stored = localStorage.getItem(FIELD_PREF_STORAGE_KEY);
                    if (stored) {
                        const parsed = JSON.parse(stored);
                        if (Array.isArray(parsed)) {
                            // Only keep values that exist in meta
                            const allowed = new Set(
                                groups.flatMap(g => (g.fields || []).map(f => f.key))
                            );
                            initialFields = parsed.filter(key => allowed.has(key));
                        }
                    }
                } catch (e) {
                    // ignore localStorage errors and fall back to defaults
                }

                if (!initialFields || initialFields.length === 0) {
                    // Use backend defaults (isDefault) if no stored preference
                    initialFields = groups.flatMap(g =>
                        (g.fields || [])
                            .filter(f => f.isDefault)
                            .map(f => f.key)
                    );
                }

                setSelectedFields(initialFields);
            } catch (err) {
                const message =
                    err.response?.data ??
                    err.message ??
                    'Unable to load export field metadata.';
                setError(message);
            } finally {
                setIsFieldMetaLoading(false);
            }
        };

        fetchMeta();
    }, [open, fieldGroups]);

    const handleFilterModeChange = (event) => {
        const value = event.target.value;
        setFilterMode(value);
        setError(null);
    };

    const handleMonthYearChange = (value) => {
        setMonthYear(value);
        setError(null);
    };

    const handleStartDateChange = (value) => {
        setStartDate(value);
        setError(null);
    };

    const handleEndDateChange = (value) => {
        setEndDate(value);
        setError(null);
    };

    const handleFieldsChange = (event) => {
        const { target: { value } } = event;
        const newValues = typeof value === 'string' ? value.split(',') : value;
        setSelectedFields(newValues);
        setError(null);

        try {
            localStorage.setItem(FIELD_PREF_STORAGE_KEY, JSON.stringify(newValues));
        } catch (e) {
            // ignore localStorage write errors
        }
    };

    const handleFileTypeChange = (event) => {
        setFileType(event.target.value);
        setError(null);
    };

    const handleExport = async () => {
        if (hasError) return;

        setIsExporting(true);
        setError(null);

        try {
            const params = {
                filetype: fileType,
            };

            if (filterMode === FILTER_MODE.MONTH) {
                const m = monthYear || thisMonth;
                params.year = m.year();
                // backend expects 1–12
                params.month = m.month() + 1;
            } else {
                params.startDate = dayjs(startDate).format('YYYY-MM-DD');
                params.endDate = dayjs(endDate).format('YYYY-MM-DD');
            }

            if (selectedFields && selectedFields.length > 0) {
                params.fields = selectedFields.join(',');
            }

            const response = await orderApiV2.get('/export', {
                params,
                responseType: 'blob',
            });

            const disposition = response.headers['content-disposition'];
            let filename = 'orders_export.csv';

            if (disposition) {
                const match = /filename="?([^";]+)"?/i.exec(disposition);
                if (match && match[1]) {
                    filename = match[1];
                }
            }

            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            setIsExporting(false);
            onClose && onClose();
        } catch (err) {
            const message =
                err.response?.data ??
                err.message ??
                'Something went wrong while exporting orders.';
            setError(message);
            setIsExporting(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Export Orders</DialogTitle>
                <DialogContent dividers>
                    {isExporting && <LinearProgress sx={{ mb: 2 }} />}

                    {error && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        </Box>
                    )}

                    {/* Date filter mode */}
                    <Box sx={{ mb: 3 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Date filter</FormLabel>
                            <RadioGroup
                                row
                                value={filterMode}
                                onChange={handleFilterModeChange}
                            >
                                <FormControlLabel
                                    value={FILTER_MODE.MONTH}
                                    control={<Radio size="small" />}
                                    label="By Month"
                                />
                                <FormControlLabel
                                    value={FILTER_MODE.CUSTOM}
                                    control={<Radio size="small" />}
                                    label="Custom Date Range"
                                />
                            </RadioGroup>
                        </FormControl>

                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {filterMode === FILTER_MODE.MONTH && (
                                <FormControl sx={{ minWidth: 200 }}>
                                    <DatePicker
                                        label="Month / Year"
                                        views={['month', 'year']}
                                        value={monthYear}
                                        onChange={handleMonthYearChange}
                                        slotProps={{
                                            textField: { size: 'small' },
                                        }}
                                    />
                                </FormControl>
                            )}

                            {filterMode === FILTER_MODE.CUSTOM && (
                                <>
                                    <FormControl sx={{ minWidth: 200 }}>
                                        <DatePicker
                                            label="Start date"
                                            value={startDate}
                                            onChange={handleStartDateChange}
                                            slotProps={{
                                                textField: { size: 'small' },
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl sx={{ minWidth: 200 }}>
                                        <DatePicker
                                            label="End date"
                                            value={endDate}
                                            onChange={handleEndDateChange}
                                            slotProps={{
                                                textField: { size: 'small' },
                                            }}
                                        />
                                    </FormControl>
                                </>
                            )}
                        </Box>

                        {dateError && (
                            <FormHelperText error sx={{ mt: 1 }}>
                                {dateError}
                            </FormHelperText>
                        )}
                    </Box>

                    {/* Fields selection */}
                    <Box sx={{ mb: 3 }}>
                        <FormControl fullWidth error={!!fieldsError} disabled={isFieldMetaLoading || !fieldGroups}>
                            <FormLabel>Fields</FormLabel>
                            {isFieldMetaLoading && (
                                <Box sx={{ mb: 1 }}>
                                    <LinearProgress />
                                </Box>
                            )}
                            <Select
                                multiple
                                size="small"
                                value={selectedFields}
                                onChange={handleFieldsChange}
                                renderValue={(selected) => {
                                    const labels = flatFieldOptions
                                        .filter(opt => selected.includes(opt.value))
                                        .map(opt => opt.label);
                                    return labels.join(', ');
                                }}>
                                {fieldGroups &&
                                    fieldGroups.flatMap((group) => {
                                        const groupItems = (group.fields || []).map((field) => (
                                            <MenuItem key={field.key} value={field.key}>
                                                <Checkbox checked={selectedFields.indexOf(field.key) > -1} />
                                                <ListItemText primary={field.label} />
                                            </MenuItem>
                                        ));

                                        return [
                                            <ListSubheader key={`${group.key}-header`}>
                                                {group.label}
                                            </ListSubheader>,
                                            ...groupItems,
                                        ];
                                    })}
                            </Select>
                            {fieldsError && (
                                <FormHelperText>{fieldsError}</FormHelperText>
                            )}
                        </FormControl>
                    </Box>

                    {/* File type selection */}
                    <Box sx={{ mb: 1 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">File type</FormLabel>
                            <RadioGroup
                                row
                                value={fileType}
                                onChange={handleFileTypeChange}
                            >
                                {FILE_TYPES.map((type) => (
                                    <FormControlLabel
                                        key={type}
                                        value={type}
                                        control={<Radio size="small" />}
                                        label={type}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                        Exports will be generated as CSV files based on your selections.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={isExporting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExport}
                        disabled={isExporting || hasError}
                        variant="contained"
                    >
                        {isExporting ? 'Exporting…' : 'Export'}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default ExportOrdersModal;